import { captureException } from '@sentry/react';
import axios, { AxiosResponse, CanceledError, CreateAxiosDefaults, InternalAxiosRequestConfig } from 'axios';

import { authSessionService, withAccountSessionLock } from '@/services/auth/sessions';
import { toApiServiceError } from '@/services/http/errors';
import { useAuthStore } from '@/stores/auth';
import { useMultiAccountStore } from '@/stores/auth/multiAccount';

declare module 'axios' {
    export interface AxiosRequestConfig {
        suppressErrorCapture?: boolean;
        accountSessionGeneration?: number;
        accountContextId?: string;
        _retry?: boolean;
    }
}

export const authorizedAxiosInstanceConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.PROD ? 'https://api.countercyclical.io' : 'http://localhost:3000',
    timeout: 10000,
    withCredentials: true,
    maxContentLength: 50 * 1000 * 1000,
    maxBodyLength: 50 * 1000 * 1000,
};

export function bindAccountRequest(config: InternalAxiosRequestConfig) {
    const state = useMultiAccountStore.getState();
    if (
        state.switchingAccount ||
        (config.accountSessionGeneration !== undefined && config.accountSessionGeneration !== state.generation)
    ) {
        throw new CanceledError('Account session changed');
    }
    config.accountSessionGeneration = state.generation;
    config.accountContextId = state.context?.accountId;
    if (state.context) {
        config.headers.set('X-Countercyclical-Account-Id', state.context.accountId);
    }
    if (state.context?.workspaceId) {
        config.headers.set('X-Countercyclical-Workspace-Id', state.context.workspaceId);
    }
    const token = useAuthStore.getState().accessToken;
    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
}

export function acceptAccountResponse(response: AxiosResponse) {
    const state = useMultiAccountStore.getState();
    if (state.switchingAccount || response.config.accountSessionGeneration !== state.generation) {
        throw new CanceledError('Discarded response from the previous account session');
    }
    return response;
}

export const authorizedAxiosInstance = axios.create(authorizedAxiosInstanceConfig);
let refreshPromise: Promise<string> | null = null;

authorizedAxiosInstance.interceptors.request.use(bindAccountRequest);
authorizedAxiosInstance.interceptors.response.use(acceptAccountResponse, async (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.config) {
        return Promise.reject(error);
    }
    const original = error.config;
    const state = useMultiAccountStore.getState();
    if (state.switchingAccount || original.accountSessionGeneration !== state.generation) {
        throw new CanceledError('Account session changed');
    }
    if (error.response?.status === 409 && error.response.data?.code === 'SESSION_CONTEXT_CHANGED') {
        useMultiAccountStore.setState({ switchingAccount: true });
        window.location.assign('/');
        throw new CanceledError('Account session changed in another tab');
    }
    if (error.response?.status !== 401 || original._retry) {
        return Promise.reject(error);
    }
    original._retry = true;
    if (!refreshPromise) {
        refreshPromise = withAccountSessionLock(async () => {
            if (useMultiAccountStore.getState().generation !== original.accountSessionGeneration) {
                throw new CanceledError('Account session changed');
            }
            const result = await authSessionService.refreshAccount(original.accountContextId);
            if (
                useMultiAccountStore.getState().generation !== original.accountSessionGeneration ||
                (original.accountContextId && result.accountId !== original.accountContextId)
            ) {
                throw new CanceledError('Account session changed');
            }
            await useAuthStore.getState().setAccessToken(result.accessToken);
            return result.accessToken;
        }).finally(() => {
            refreshPromise = null;
        });
    }
    try {
        const token = await refreshPromise;
        original.headers.set('Authorization', `Bearer ${token}`);
        return authorizedAxiosInstance(original);
    } catch (refreshError) {
        if (original.suppressErrorCapture && refreshError instanceof Error) {
            Object.assign(refreshError, { suppressErrorCapture: true });
        }
        throw refreshError;
    }
});

authorizedAxiosInstance.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }
        const apiError = toApiServiceError(error);
        const suppressed =
            (axios.isAxiosError(error) && error.config?.suppressErrorCapture) ||
            (error instanceof Error && 'suppressErrorCapture' in error && error.suppressErrorCapture);
        if (!suppressed) {
            captureException(apiError);
        } else {
            Object.assign(apiError, { suppressErrorCapture: true });
        }
        return Promise.reject(apiError);
    },
);
